import React, { useState, useEffect } from 'react';
import { Dropdown, Button, Avatar, Space, Modal, notification } from 'antd';
import { UserOutlined, LogoutOutlined, SettingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { auth, signOut } from '../../pages/Auth/db/firebase';
import UserSettingsModal from './UserSettingsModal';
import { sendHelpNotification } from './NotificationSystem';

const UserDropdown = ({ userData }) => {
  const [isSettingsVisible, setIsSettingsVisible] = useState(false);
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);
  const [user, setUser] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    console.log('Logout clicked');
    setIsLogoutModalVisible(true);
  };

  const confirmLogout = async () => {
    console.log('Confirming logout');
    setLoggingOut(true);
    try {
      await signOut(auth);
      api.success({
        message: 'Logged out successfully',
        placement: 'bottomRight',
      });
      setIsLogoutModalVisible(false);
      // Navigation will be handled by auth state change
    } catch (error) {
      console.error('Logout error:', error);
      api.error({
        message: 'Logout failed',
        description: error.message,
        placement: 'bottomRight',
      });
    } finally {
      setLoggingOut(false);
    }
  };

  const cancelLogout = () => {
    setIsLogoutModalVisible(false);
  };

  const handleSettings = () => {
    setIsSettingsVisible(true);
  };

  const handleHelp = async () => {
    try {
      await sendHelpNotification({
        userId: user?.uid,
        userEmail: user?.email,
        userName: userData?.username || user?.displayName || 'Unknown User',
        userRole: userData?.role || 'Unknown Role',
        facilityId: userData?.facilityId || 'Unknown Facility',
        timestamp: new Date().toISOString(),
        message: 'User requested help from the system'
      });
      
      api.success({
        message: 'Help Request Sent',
        description: 'Your help request has been sent to the super admin.',
        placement: 'bottomRight',
      });
    } catch (error) {
      api.error({
        message: 'Help Request Failed',
        description: 'Failed to send help request. Please try again.',
        placement: 'bottomRight',
      });
    }
  };

  const menuItems = [
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: handleSettings,
    },
    {
      key: 'help',
      icon: <QuestionCircleOutlined />,
      label: 'Help',
      onClick: handleHelp,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ];

  const displayName = userData?.username || user?.displayName || user?.email || 'User';
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <>
      {contextHolder}
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button type="text" className="user-dropdown-button">
          <Space>
            <Avatar 
              size="small" 
              icon={<UserOutlined />}
              style={{ 
                backgroundColor: 'var(--primary-color)',
                color: 'var(--text-inverse)'
              }}
            >
              {initials}
            </Avatar>
            <span style={{ color: 'var(--text-primary)' }}>{displayName}</span>
          </Space>
        </Button>
      </Dropdown>

      <UserSettingsModal
        visible={isSettingsVisible}
        onClose={() => setIsSettingsVisible(false)}
        userData={userData}
        user={user}
      />

      <Modal
        title="Confirm Logout"
        open={isLogoutModalVisible}
        onOk={confirmLogout}
        onCancel={cancelLogout}
        okText="Yes, Logout"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        confirmLoading={loggingOut}
      >
        <p>Are you sure you want to log out?</p>
      </Modal>
    </>
  );
};

export default UserDropdown;
